"use client";

import { useState, useEffect, useCallback } from "react";
import { createClient, createHiveClient } from "@/lib/supabase/client";
import {
  Camera,
  Wand2,
  Loader2,
  X,
  Paperclip,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MagicWandSelector } from "./magic-wand-selector";
import { FeedbackType } from "./feedback-widget";
import html2canvas from "html2canvas-pro";

interface ElementData {
  id: string; // Unique ID for the selection
  tagName: string;
  elementId?: string;
  className?: string;
  textContent?: string;
  attributes: Record<string, string>;
  xpath: string;
  screenshot?: string;
  htmlSnippet?: string;
  boundingBox: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
  aiDescription?: string;
}

interface ScreenshotData {
  id: string;
  url: string;
  file: Blob;
}

interface FeedbackDialogProps {
  type: FeedbackType;
  open: boolean;
  onClose: () => void;
}

interface ConsoleLogs {
  logs: Array<{
    level: string;
    message: string;
    timestamp: number;
  }>;
}

export function FeedbackDialog({ type, open, onClose }: FeedbackDialogProps) {
  const authClient = createClient(); // For auth
  const hiveClient = createHiveClient(); // For feedback data
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<
    "low" | "medium" | "high" | "critical"
  >("medium");
  const [screenshots, setScreenshots] = useState<ScreenshotData[]>([]);
  const [logs, setLogs] = useState<ConsoleLogs>({ logs: [] });
  const [elements, setElements] = useState<ElementData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [isMagicWandActive, setIsMagicWandActive] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Add/remove body class to push content when panel is open
  useEffect(() => {
    if (open) {
      document.body.classList.add("feedback-panel-open");
    } else {
      document.body.classList.remove("feedback-panel-open");
    }

    return () => {
      document.body.classList.remove("feedback-panel-open");
    };
  }, [open]);

  // Capture console logs
  useEffect(() => {
    if (!open) return;

    const capturedLogs: ConsoleLogs = { logs: [] };
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
    };

    const captureLog = (level: string, ...args: any[]) => {
      capturedLogs.logs.push({
        level,
        message: args
          .map((arg) =>
            typeof arg === "object" ? JSON.stringify(arg) : String(arg)
          )
          .join(" "),
        timestamp: Date.now(),
      });
      // Keep logs limited to last 50
      if (capturedLogs.logs.length > 50) {
        capturedLogs.logs.shift();
      }
    };

    console.log = (...args) => {
      captureLog("log", ...args);
      originalConsole.log(...args);
    };
    console.warn = (...args) => {
      captureLog("warn", ...args);
      originalConsole.warn(...args);
    };
    console.error = (...args) => {
      captureLog("error", ...args);
      originalConsole.error(...args);
    };
    console.info = (...args) => {
      captureLog("info", ...args);
      originalConsole.info(...args);
    };

    setLogs(capturedLogs);

    return () => {
      console.log = originalConsole.log;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      console.info = originalConsole.info;
    };
  }, [open]);

  const captureScreenshot = async () => {
    try {
      setIsCapturingScreenshot(true);

      // Wait for dialog to fade out
      await new Promise((resolve) => setTimeout(resolve, 200));

      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: 1,
      });

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b!), "image/png");
      });

      const newScreenshot: ScreenshotData = {
        id: `screenshot-${Date.now()}`,
        url: URL.createObjectURL(blob),
        file: blob,
      };

      setScreenshots((prev) => [...prev, newScreenshot]);
    } catch (error) {
      console.error("Screenshot capture failed:", error);
    } finally {
      setIsCapturingScreenshot(false);
    }
  };

  const removeScreenshot = (id: string) => {
    setScreenshots((prev) => {
      const screenshot = prev.find((s) => s.id === id);
      if (screenshot) {
        URL.revokeObjectURL(screenshot.url);
      }
      return prev.filter((s) => s.id !== id);
    });
  };

  const handleElementSelect = async (
    data: Omit<ElementData, "id" | "aiDescription">
  ) => {
    const elementId = `element-${Date.now()}`;
    setIsMagicWandActive(false);
    setIsGeneratingAI(true);

    // Add element with temporary AI description
    const newElement: ElementData = {
      ...data,
      id: elementId,
      aiDescription: undefined,
    };
    setElements((prev) => [...prev, newElement]);

    // Generate AI description in background
    try {
      const response = await fetch("/api/feedback/enhance-element", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ elementData: data }),
      });

      if (response.ok) {
        const { description } = await response.json();
        setElements((prev) =>
          prev.map((el) =>
            el.id === elementId ? { ...el, aiDescription: description } : el
          )
        );
      }
    } catch (error) {
      console.error("AI description generation failed:", error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const removeElement = (id: string) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
  };

  const getBrowserInfo = () => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        availWidth: window.screen.availWidth,
        availHeight: window.screen.availHeight,
        colorDepth: window.screen.colorDepth,
      },
    };
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();

      console.log("User authenticated:", user?.id);

      if (!user) throw new Error("User not authenticated");

      // Get the auth session and set it on hiveClient
      const {
        data: { session },
      } = await authClient.auth.getSession();
      if (session) {
        await hiveClient.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
      }

      // Upload all screenshots
      const screenshotUrls: string[] = [];
      for (const screenshot of screenshots) {
        const fileName = `${user.id}/${Date.now()}-${screenshot.id}.png`;
        const { error: uploadError } = await hiveClient.storage
          .from("feedback-screenshots")
          .upload(fileName, screenshot.file, {
            contentType: "image/png",
            upsert: false,
          });

        if (uploadError) {
          console.warn("Screenshot upload failed:", uploadError.message);
        } else {
          const { data: urlData } = hiveClient.storage
            .from("feedback-screenshots")
            .getPublicUrl(fileName);
          screenshotUrls.push(urlData.publicUrl);
        }
      }

      const feedbackData = {
        user_id: user.id,
        type,
        priority,
        title: title.trim(),
        description: description.trim(),
        screenshot_url: screenshotUrls[0] || null, // Primary screenshot (first one)
        logs: logs.logs.slice(-20), // Last 20 logs
        element_data: {
          screenshots: screenshotUrls, // All screenshots
          elements: elements, // All selected elements with AI descriptions
        },
        page_url: window.location.href,
        page_title: document.title,
        browser_info: getBrowserInfo(),
        viewport_size: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      };

      console.log("Submitting feedback data:", feedbackData);

      // Insert feedback to HIVE/MSTRO-MVP database
      const { data: insertData, error: insertError } = await hiveClient
        .from("user_feedback")
        .insert(feedbackData)
        .select();

      console.log("Insert result:", insertData);
      console.log("Insert error:", insertError);

      if (insertError) {
        console.error(
          "Insert error details:",
          JSON.stringify(insertError, null, 2)
        );
        console.error("Error code:", insertError.code);
        console.error("Error message:", insertError.message);
        console.error("Error hint:", insertError.hint);
        console.error("Error details:", insertError.details);
        throw new Error(
          insertError.message ||
            insertError.hint ||
            `Database error: ${insertError.code}` ||
            "Failed to save feedback"
        );
      }

      console.log("Feedback submitted successfully:", insertData);

      // Show success state
      setSubmitSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setTitle("");
        setDescription("");
        setPriority("medium");
        setScreenshots([]);
        setElements([]);
        setSubmitSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error("Feedback submission failed:", error);
      const errorMessage =
        error?.message || "Failed to submit feedback. Please try again.";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeLabels = {
    bug: "Report a Bug",
    feature: "Request a Feature",
    design: "Design Feedback",
    improvement: "Suggest Improvement",
    question: "Ask a Question",
    other: "Other Feedback",
  };

  // Hide panel when using tools, but keep form state
  const shouldHidePanel = isCapturingScreenshot || isMagicWandActive;

  if (!open) return null;

  return (
    <>
      {/* Backdrop overlay (optional, only on smaller screens) */}
      {!shouldHidePanel && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9997] transition-opacity duration-300 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar panel - slides in from right */}
      <div
        data-feedback-dialog
        className={`fixed top-0 right-0 h-screen w-full sm:w-[90vw] md:w-[520px] lg:w-[560px] bg-background border-l border-border shadow-2xl z-[9998] transform transition-transform duration-300 ease-in-out ${
          shouldHidePanel
            ? "translate-x-full pointer-events-none"
            : "translate-x-0"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold">{typeLabels[type]}</h2>

          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="shrink-0"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(100vh-88px)]">
          <ScrollArea className="flex-1">
            <div className="p-6 pb-24">
              {submitSuccess ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">
                    Feedback Submitted!
                  </h3>
                  <p className="text-muted-foreground text-center">
                    Thank you for helping us improve the app.
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {/* Priority */}
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <Select
                        value={priority}
                        onValueChange={(v: any) => setPriority(v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-[9999]">
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Title */}
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        placeholder="Brief summary of your feedback"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        placeholder="Provide detailed information about your feedback..."
                        rows={6}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>

                    {/* Tools */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={captureScreenshot}
                        disabled={isCapturingScreenshot}
                      >
                        {isCapturingScreenshot ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Camera className="h-4 w-4 mr-2" />
                        )}
                        Capture Screenshot
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsMagicWandActive(true)}
                        disabled={isMagicWandActive}
                      >
                        <Wand2 className="h-4 w-4 mr-2" />
                        Magic Wand
                      </Button>

                      {screenshots.length > 0 && (
                        <Badge variant="secondary">
                          <Paperclip className="h-3 w-3 mr-1" />
                          {screenshots.length} screenshot
                          {screenshots.length > 1 ? "s" : ""}
                        </Badge>
                      )}

                      {elements.length > 0 && (
                        <Badge variant="secondary">
                          <Wand2 className="h-3 w-3 mr-1" />
                          {elements.length} element
                          {elements.length > 1 ? "s" : ""}
                        </Badge>
                      )}

                      {logs.logs.length > 0 && (
                        <Badge variant="secondary">
                          {logs.logs.length} console logs captured
                        </Badge>
                      )}
                    </div>

                    {/* AI Description Status */}
                    {isGeneratingAI && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating AI description...
                      </div>
                    )}

                    {/* Selected Elements */}
                    {elements.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-xs font-semibold">
                          Selected Elements ({elements.length})
                        </Label>
                        {elements.map((element) => (
                          <div
                            key={element.id}
                            className="border rounded-lg overflow-hidden bg-muted"
                          >
                            <div className="p-3 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-mono text-muted-foreground truncate">
                                    {element.tagName}
                                    {element.elementId &&
                                      `#${element.elementId}`}
                                    {element.className &&
                                      `.${element.className.split(" ")[0]}`}
                                  </p>
                                  {element.aiDescription && (
                                    <p className="text-sm mt-2">
                                      {element.aiDescription}
                                    </p>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 shrink-0"
                                  onClick={() => removeElement(element.id)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                              {element.screenshot && (
                                <div className="border rounded overflow-hidden bg-background p-2">
                                  <img
                                    src={element.screenshot}
                                    alt="Element preview"
                                    className="max-w-full h-auto"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Screenshots */}
                    {screenshots.length > 0 && (
                      <div className="space-y-3">
                        <Label className="text-xs font-semibold">
                          Screenshots ({screenshots.length})
                        </Label>
                        {screenshots.map((screenshot) => (
                          <div
                            key={screenshot.id}
                            className="border rounded-lg overflow-hidden bg-muted relative group"
                          >
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              onClick={() => removeScreenshot(screenshot.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <img
                              src={screenshot.url}
                              alt="Screenshot preview"
                              className="w-full h-auto"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Fixed Submit Actions at Bottom */}
          {!submitSuccess && (
            <div className="flex justify-end gap-2 p-4 border-t border-border bg-background shrink-0">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || !title.trim() || !description.trim()}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Feedback"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Magic Wand Selector */}
      <MagicWandSelector
        isActive={isMagicWandActive}
        onElementSelect={handleElementSelect}
        onCancel={() => setIsMagicWandActive(false)}
      />
    </>
  );
}
